import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sparkles, Power, RotateCw, FlaskConical, CheckCircle2, Info,
  Users, Shield, Calendar as CalIcon, MessageSquare, Lock,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const DEFAULT_STATS = { players: 0, clubs: 0, interactions: 0, reminders: 0, events: 0 };

/**
 * DemoModeCard — premium settings card for the demo mode toggle.
 * Two states: OFF (compact CTA) and ON (rich info + stats + actions + security note).
 */
export default function DemoModeCard() {
  const { tf } = useLang();
  const { user } = useAuth();
  const demoMode = !!user?.demo_mode;
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // "enable" | "disable" | "reset" | null
  const [stats, setStats] = useState(DEFAULT_STATS);

  const refreshStats = useCallback(async () => {
    if (!demoMode) { setStats(DEFAULT_STATS); return; }
    try {
      const r = await api.get("/demo/stats");
      setStats(r.data);
    } catch (e) {
      console.error("[DemoModeCard] stats fetch failed:", e);
    }
  }, [demoMode]);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  const run = async (action) => {
    setBusy(true);
    try {
      if (action === "enable") {
        await api.post("/demo/enable");
        toast.success(tf("Mode démo activé"));
      } else if (action === "disable") {
        await api.post("/demo/disable");
        toast.success(tf("Mode démo désactivé — vraies données préservées"));
      } else if (action === "reset") {
        await api.post("/demo/reset");
        toast.success(tf("Données démo réinitialisées"));
      }
      // Hard reload to guarantee no stale demo data lingers in any page state
      // (Dashboard, Pipeline, Clubs, Calendar, Relances, NotificationsBell each cache their own).
      // Small delay so the toast is visible to the user before the refresh.
      setTimeout(() => {
        window.location.reload();
      }, 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Une erreur est survenue"));
      setBusy(false);
      setConfirm(null);
    }
  };

  const dialogConfig = {
    enable: {
      title: tfr("Activer le mode démo ?"),
      description: tfr("Cela ajoutera ~13 joueurs, 7 clubs et un agenda fictif à votre compte. Vos vraies données ne seront pas modifiées."),
      cta: "Activer",
      ctaClass: "",
    },
    disable: {
      title: tfr("Quitter le mode démo ?"),
      description: tfr("Toutes les données démo seront supprimées. Vos vraies données restent intactes."),
      cta: "Quitter",
      ctaClass: "bg-danger text-danger-foreground hover:bg-danger/90",
    },
    reset: {
      title: tfr("Réinitialiser les données démo ?"),
      description: tfr("Les données démo actuelles seront remplacées par un nouveau jeu propre. Vos vraies données restent intactes."),
      cta: tfr("Réinitialiser"),
      ctaClass: "",
    },
  };
  const cfg = confirm ? dialogConfig[confirm] : null;

  return (
    <>
      <Card
        className={cn(
          "bg-surface transition-colors",
          demoMode && "ring-1 ring-info/30"
        )}
        data-testid="demo-mode-card"
        data-active={demoMode ? "true" : "false"}
      >
        <CardContent className="p-5 md:p-6 space-y-6">
          {/* === HEADER === */}
          <header className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold tracking-tight">{tf("Données de démo")}</h3>
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] gap-1",
                  demoMode
                    ? "border-info/40 bg-info/10 text-info"
                    : "border-border bg-secondary/50 text-muted-foreground"
                )}
                data-testid="demo-mode-badge"
              >
                <FlaskConical className="h-3 w-3" />
                {demoMode ? tf("Activé") : tf("Désactivé")}
              </Badge>
            </div>
            {demoMode && (
              <Badge
                className="text-[10px] border-success/40 bg-success/10 text-success gap-1"
                variant="outline"
                data-testid="demo-mode-active-badge"
              >
                <CheckCircle2 className="h-3 w-3" />
                {tf("Mode démo actif")}
              </Badge>
            )}
          </header>

          {/* === INFO BLOCK === */}
          <InfoBlock demoMode={demoMode} />

          {/* === STATS GRID (only when active) === */}
          {demoMode && <StatsGrid stats={stats} />}

          {/* === ACTIONS === */}
          {!demoMode ? (
            <button
              type="button"
              onClick={() => setConfirm("enable")}
              disabled={busy}
              className={cn(
                "group w-full flex items-center justify-center gap-2 rounded-xl",
                "border border-border bg-secondary/30 hover:bg-secondary/60 hover:border-foreground/25",
                "px-4 py-3 transition-all duration-200 text-sm font-medium",
                busy && "opacity-50 cursor-not-allowed"
              )}
              data-testid="demo-enable-btn"
            >
              <Sparkles className="h-4 w-4 text-info" />
              {tf("Activer le mode démo")}
            </button>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ActionButton
                icon={RotateCw}
                title={tf("Reset démo")}
                description={tf("Recharger les données démo")}
                onClick={() => setConfirm("reset")}
                disabled={busy}
                testid="demo-reset-btn"
              />
              <ActionButton
                icon={Power}
                title={tf("Quitter le mode démo")}
                description={tf("Supprimer toutes les données démo")}
                onClick={() => setConfirm("disable")}
                disabled={busy}
                tone="danger"
                testid="demo-disable-btn"
              />
            </div>
          )}

          {/* === SECURITY NOTE (only when active) === */}
          {demoMode && (
            <div
              className="flex items-start gap-2.5 pt-1 text-[12px] leading-relaxed text-muted-foreground"
              data-testid="demo-security-note"
            >
              <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground/70" aria-hidden />
              <p>
                {tf("Seules les données de démo seront supprimées.")}
                <span className="block text-foreground/70">{tf("Vos vraies données sont 100 % sécurisées.")}</span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <AlertDialogContent
          className="bg-surface border-border"
          data-testid="demo-confirm-dialog"
        >
          {cfg && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{tf(cfg.title)}</AlertDialogTitle>
                <AlertDialogDescription>{tf(cfg.description)}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy} data-testid="demo-confirm-cancel">
                  {tf("Annuler")}
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={() => run(confirm)}
                  className={cn(cfg.ctaClass)}
                  data-testid="demo-confirm-action"
                >
                  {busy ? "..." : tf(cfg.cta)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ============== sub-components ============== */

function InfoBlock({ demoMode }) {
  const { tf } = useLang();
  return (
    <div
      className={cn(
        "rounded-2xl border p-4 md:p-5 flex items-start gap-3.5",
        demoMode
          ? "bg-info/[0.06] border-info/25"
          : "bg-secondary/30 border-border"
      )}
      data-testid="demo-info-block"
    >
      <div
        className={cn(
          "shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center",
          demoMode ? "bg-info/15 text-info" : "bg-secondary/50 text-muted-foreground"
        )}
        aria-hidden
      >
        <Info className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0 max-w-prose">
        <div className="text-sm font-medium">
          {demoMode ? tf("Mode démo activé") : tf("Mode démo désactivé")}
        </div>
        <p className="text-[12.5px] leading-relaxed text-muted-foreground mt-1">
          {demoMode ? (
            <>
              {tf("Vos vraies données et les données de démo coexistent.")}
              <br className="hidden sm:inline" />
              {tf("Vous pouvez tester librement sans impacter vos données réelles.")}
            </>
          ) : (
            <>
              {tf("Activez pour remplir votre compte avec ~13 joueurs, 7 clubs et un agenda réaliste. Idéal pour tester la plateforme ou présenter à un prospect.")}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function StatsGrid({ stats }) {
  // Combine reminders + events into a single "événements" count to match the design intent.
  const eventsCount = (stats.events || 0) + (stats.reminders || 0);
  const items = [
    { Icon: Users, value: `~${stats.players ?? 0}`, label: "joueurs", iconClass: "text-info" },
    { Icon: Shield, value: stats.clubs ?? 0, label: stats.clubs === 1 ? "club" : "clubs", iconClass: "text-success" },
    { Icon: CalIcon, value: eventsCount, label: eventsCount === 1 ? tfr("événement") : tfr("événements"), iconClass: "text-warning" },
    { Icon: MessageSquare, value: stats.interactions ?? 0, label: "interactions", iconClass: "text-[#A78BFA]" },
  ];
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-3"
      data-testid="demo-stats-grid"
    >
      {items.map((it) => (
        <StatTile key={it.label} {...it} />
      ))}
    </div>
  );
}

function StatTile({ Icon, value, label, iconClass }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-secondary/30 px-3 py-4 md:py-5 text-center",
        "transition-all duration-200",
        "hover:border-foreground/25 hover:bg-secondary/60 hover:shadow-[0_0_24px_-12px_rgba(255,255,255,0.15)]"
      )}
      data-testid={`demo-stat-${label}`}
    >
      <div className={cn("flex justify-center mb-2", iconClass)}>
        <Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden />
      </div>
      <div className="text-xl md:text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 lowercase">{label}</div>
    </div>
  );
}

function ActionButton({ icon: Icon, title, description, onClick, disabled, tone, testid }) {
  const danger = tone === "danger";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "group flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-200",
        danger
          ? "border-danger/25 bg-danger/[0.04] hover:bg-danger/10 hover:border-danger/40 text-danger"
          : "border-border bg-secondary/30 hover:bg-secondary/60 hover:border-foreground/25",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      data-testid={testid}
    >
      <span
        className={cn(
          "shrink-0 h-9 w-9 rounded-full flex items-center justify-center border transition-transform group-hover:scale-105",
          danger
            ? "bg-danger/10 border-danger/25 text-danger"
            : "bg-secondary/50 border-border text-foreground"
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className={cn("block text-sm font-medium truncate", !danger && "text-foreground")}>
          {title}
        </span>
        <span className={cn("block text-[11px] mt-0.5 truncate", danger ? "text-danger/70" : "text-muted-foreground")}>
          {description}
        </span>
      </span>
    </button>
  );
}
